/**
 * RoleManagementPage — `/security/roles`.
 *
 * Lists roles from service-identity (`GET /v1/identity/roles`), lets the
 * operator view the permissions attached to each role
 * (`GET /v1/identity/roles/:id/permissions`), create non-system roles
 * (`POST /v1/identity/roles`), and edit non-system roles
 * (`PATCH /v1/identity/roles/:id`).
 *
 * Requires `Identity/readRole` — granted to dev-superadmin-role via
 * 20260522_b3_security_admin_perms.sql. Create + update additionally require
 * `Identity/createRole` / `Identity/updateRole` (granted via
 * 20260509_security_admin_full_perms.sql).
 */
import { type JSX, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Lock,
  Pencil,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type PermissionActionDto,
  type PermissionCatalogDto,
  type PermissionModuleDto,
  type RoleDto,
  createRole,
  getPermissionCatalog,
  listRolePermissions,
  listRoles,
  updateRole,
} from '../../lib/api/identity-admin';
import { PageHeader } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

function PermissionsDrawer({ roleId }: { roleId: string }): JSX.Element {
  const perms = useQuery({
    queryKey: ['identity', 'roles', roleId, 'permissions'],
    queryFn: () => listRolePermissions(roleId),
    retry: false,
  });

  if (perms.isPending) return <p className="text-xs text-muted px-4 py-2">Loading permissions…</p>;
  if (perms.isError) return <p className="text-xs text-danger px-4 py-2">{(perms.error as Error).message}</p>;
  if ((perms.data?.length ?? 0) === 0) return <p className="text-xs text-muted px-4 py-2">No permissions assigned.</p>;

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-border">
      <div className="flex flex-wrap gap-1.5">
        {(perms.data ?? []).map((p) => (
          <span
            key={p}
            className="px-1.5 py-0.5 text-[10px] font-mono bg-white border border-border rounded text-gray-700"
          >
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Permission tree — checkbox UI driven by `GET /v1/identity/permissions/catalog`
// ---------------------------------------------------------------------------

/** Module → set of granted permission strings the tree mutates in place. */
type GrantSet = ReadonlySet<string>;

/** Tri-state value for a module-level checkbox. */
type ModuleState = 'none' | 'some' | 'all';

function moduleState(module: PermissionModuleDto, granted: GrantSet): ModuleState {
  const total = module.actions.length;
  let on = 0;
  for (const a of module.actions) {
    if (granted.has(a.permission)) on++;
  }
  if (on === 0) return 'none';
  if (on === total) return 'all';
  return 'some';
}

/** Filter the catalog by a free-text needle (module name or permission string). */
function filterCatalog(
  catalog: PermissionCatalogDto,
  needle: string,
): readonly PermissionModuleDto[] {
  const q = needle.trim().toLowerCase();
  if (!q) return catalog.modules;
  const out: PermissionModuleDto[] = [];
  for (const m of catalog.modules) {
    if (m.module.toLowerCase().includes(q)) {
      out.push(m);
      continue;
    }
    const matched = m.actions.filter((a) => a.permission.toLowerCase().includes(q));
    if (matched.length > 0) out.push({ module: m.module, actions: matched });
  }
  return out;
}

function PermissionTree({
  catalog,
  granted,
  setGranted,
  search,
  expandedModules,
  toggleModule,
}: {
  catalog: PermissionCatalogDto;
  granted: ReadonlySet<string>;
  setGranted: (next: Set<string>) => void;
  search: string;
  expandedModules: ReadonlySet<string>;
  toggleModule: (module: string) => void;
}): JSX.Element {
  const filtered = useMemo(
    () => filterCatalog(catalog, search),
    [catalog, search],
  );

  function toggleAction(a: PermissionActionDto): void {
    const next = new Set(granted);
    if (next.has(a.permission)) next.delete(a.permission);
    else next.add(a.permission);
    setGranted(next);
  }

  function toggleModuleAll(m: PermissionModuleDto): void {
    const state = moduleState(m, granted);
    const next = new Set(granted);
    if (state === 'all') {
      for (const a of m.actions) next.delete(a.permission);
    } else {
      // some | none → all
      for (const a of m.actions) next.add(a.permission);
    }
    setGranted(next);
  }

  if (filtered.length === 0) {
    return (
      <p className="text-xs text-muted py-4 text-center" data-testid="perm-tree-empty">
        No permissions match the filter.
      </p>
    );
  }

  return (
    <div className="space-y-1" data-testid="perm-tree">
      {filtered.map((m) => {
        const state = moduleState(m, granted);
        const expanded = expandedModules.has(m.module);
        const grantedCount = m.actions.filter((a) => granted.has(a.permission)).length;
        return (
          <div
            key={m.module}
            data-testid={`perm-module-${m.module}`}
            className="border border-border rounded-lg overflow-hidden"
          >
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50">
              <input
                type="checkbox"
                aria-label={`Toggle all ${m.module} permissions`}
                data-testid={`perm-module-checkbox-${m.module}`}
                checked={state === 'all'}
                ref={(el) => {
                  if (el) el.indeterminate = state === 'some';
                }}
                onChange={() => toggleModuleAll(m)}
              />
              <button
                type="button"
                onClick={() => toggleModule(m.module)}
                data-testid={`perm-module-toggle-${m.module}`}
                className="flex-1 flex items-center gap-1.5 text-left"
              >
                {expanded ? (
                  <ChevronDown className="w-3 h-3 text-muted" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-muted" />
                )}
                <span className="text-sm font-semibold text-gray-900">{m.module}</span>
                <span className="text-[10px] text-muted">
                  ({grantedCount} of {m.actions.length} granted)
                </span>
              </button>
            </div>
            {expanded ? (
              <ul className="divide-y divide-border bg-white">
                {m.actions.map((a) => {
                  const isGranted = granted.has(a.permission);
                  const orphan = !a.inCode;
                  return (
                    <li
                      key={a.permission}
                      data-testid={`perm-action-${a.permission}`}
                      className="px-3 py-2 flex items-start gap-2 text-xs"
                    >
                      <input
                        type="checkbox"
                        aria-label={a.permission}
                        data-testid={`perm-checkbox-${a.permission}`}
                        className="mt-0.5"
                        checked={isGranted}
                        onChange={() => toggleAction(a)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 font-mono">
                          <span className={orphan ? 'text-amber-700' : 'text-gray-900'}>
                            {a.permission}
                          </span>
                          {orphan ? (
                            <span
                              title="Granted somewhere but no controller route declares it. May be a typo or a removed endpoint."
                              data-testid={`perm-orphan-${a.permission}`}
                              className="inline-flex items-center text-amber-700"
                            >
                              <AlertTriangle className="w-3 h-3" />
                            </span>
                          ) : null}
                        </div>
                        {a.routes.length > 0 ? (
                          <ul className="mt-1 space-y-0.5 text-[10px] text-muted">
                            {a.routes.map((r, idx) => (
                              <li key={`${r.method}-${r.path}-${idx}`} className="font-mono">
                                <span className="font-semibold">{r.method}</span> {r.path}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function RoleEditorModal({
  role,
  onClose,
  onSaved,
}: {
  role: RoleDto | null; // null => create mode
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const isEdit = role !== null;
  const [id, setId] = useState(role?.id ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');

  // Tree state. `granted` is the authoritative set of permission strings we
  // POST/PATCH back to the server. `dirty` ensures we only submit perms when
  // the operator actually touched them (existing behaviour for edit mode).
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [permsDirty, setPermsDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [showRaw, setShowRaw] = useState(false);
  const [rawText, setRawText] = useState('');

  const catalog = useQuery({
    queryKey: ['identity', 'permissions', 'catalog'],
    queryFn: getPermissionCatalog,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const currentGrants = useQuery({
    queryKey: ['identity', 'roles', role?.id ?? '__new__', 'permissions'],
    queryFn: () => (role ? listRolePermissions(role.id) : Promise.resolve([])),
    enabled: isEdit,
    retry: false,
  });

  // Hydrate the granted set + raw textarea from the server once.
  useEffect(() => {
    if (!isEdit) return;
    if (currentGrants.data && !permsDirty) {
      const next = new Set(currentGrants.data);
      setGranted(next);
      setRawText([...next].sort().join('\n'));
    }
  }, [currentGrants.data, isEdit, permsDirty]);

  function applyGranted(next: Set<string>): void {
    setGranted(next);
    setPermsDirty(true);
    setRawText([...next].sort().join('\n'));
  }

  function applyRaw(text: string): void {
    setRawText(text);
    const perms = text
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setGranted(new Set(perms));
    setPermsDirty(true);
  }

  function toggleModuleExpand(moduleName: string): void {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleName)) next.delete(moduleName);
      else next.add(moduleName);
      return next;
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const permissions = [...granted];
      if (isEdit && role) {
        return updateRole(role.id, {
          name: name.trim() || undefined,
          description: description.trim() ? description.trim() : null,
          // Replace-all semantics — only send when the operator touched the tree.
          ...(permsDirty ? { permissions } : {}),
        });
      }
      return createRole({
        id: id.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        permissions,
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <div
      data-testid="role-editor-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-5 space-y-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            {isEdit ? `Edit role · ${role?.id}` : 'New role'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted hover:text-gray-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          {!isEdit ? (
            <label className="block">
              <span className="text-xs font-medium text-muted block mb-1">Role id (kebab-case) *</span>
              <input
                type="text"
                required
                value={id}
                onChange={(e) => setId(e.target.value)}
                data-testid="role-editor-id"
                placeholder="staff-counter"
                className="w-full px-3 py-2 border border-border rounded-lg font-mono"
              />
            </label>
          ) : null}
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">Display name *</span>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="role-editor-name"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="role-editor-description"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
        </div>

        <div className="border-t border-border pt-3 flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-700">
              Permissions
              <span className="text-[10px] text-muted ml-2">
                {granted.size} granted
                {catalog.data
                  ? ` · ${catalog.data.modules.length} modules in catalog`
                  : ''}
              </span>
            </span>
            <label className="text-[10px] text-muted inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                data-testid="role-editor-show-raw"
                checked={showRaw}
                onChange={(e) => setShowRaw(e.target.checked)}
              />
              Show advanced (raw)
            </label>
          </div>

          {catalog.isPending ? (
            <p className="text-xs text-muted">Loading permission catalog…</p>
          ) : catalog.isError ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p className="font-semibold">Could not load permission catalog</p>
              <p>
                {(catalog.error as Error).message}. Use the raw editor below to
                set permissions by hand.
              </p>
            </div>
          ) : null}

          {showRaw ? (
            <label className="block flex-1">
              <span className="text-[10px] text-muted block mb-1">
                One <code className="font-mono">Module/action</code> per line.
                Whitespace + commas are also accepted.
              </span>
              <textarea
                value={rawText}
                onChange={(e) => applyRaw(e.target.value)}
                rows={10}
                data-testid="role-editor-permissions"
                placeholder={'Mail/index\nMail/register'}
                className="w-full px-3 py-2 border border-border rounded-lg font-mono text-xs flex-1"
              />
            </label>
          ) : catalog.data ? (
            <div className="flex-1 overflow-auto space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-3 h-3 text-muted absolute left-2 top-2.5" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="perm-search"
                    placeholder="Filter by module or permission…"
                    className="w-full pl-7 pr-3 py-1.5 border border-border rounded-lg text-xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(granted);
                    for (const m of filterCatalog(catalog.data ?? { modules: [] }, search)) {
                      for (const a of m.actions) next.add(a.permission);
                    }
                    applyGranted(next);
                  }}
                  data-testid="perm-check-all"
                  className="px-2 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/10 whitespace-nowrap"
                  title={search ? 'Check all permissions matching the current filter' : 'Check every permission in the catalog'}
                >
                  Check all{search ? ' (filtered)' : ''}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(granted);
                    for (const m of filterCatalog(catalog.data ?? { modules: [] }, search)) {
                      for (const a of m.actions) next.delete(a.permission);
                    }
                    applyGranted(next);
                  }}
                  data-testid="perm-uncheck-all"
                  className="px-2 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/10 whitespace-nowrap"
                  title={search ? 'Uncheck all permissions matching the current filter' : 'Clear every granted permission'}
                >
                  Uncheck all{search ? ' (filtered)' : ''}
                </button>
              </div>
              <PermissionTree
                catalog={catalog.data}
                granted={granted}
                setGranted={applyGranted}
                search={search}
                expandedModules={expandedModules}
                toggleModule={toggleModuleExpand}
              />
            </div>
          ) : null}
        </div>

        {save.isError ? (
          <p className="text-sm text-danger" data-testid="role-editor-error">
            {(save.error as Error).message}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-muted border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={
              save.isPending ||
              name.trim().length === 0 ||
              (!isEdit && id.trim().length === 0)
            }
            data-testid="role-editor-submit"
            className="px-3 py-2 text-sm font-semibold text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create role'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RoleManagementPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<RoleDto | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const roles = useQuery({
    queryKey: ['identity', 'roles'],
    queryFn: listRoles,
    retry: false,
  });

  const denied =
    roles.isError &&
    roles.error instanceof ApiError &&
    (roles.error.status === 401 || roles.error.status === 403);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (roles.data ?? []).filter((r) => {
      if (!needle) return true;
      return (
        r.id.toLowerCase().includes(needle) ||
        r.name.toLowerCase().includes(needle) ||
        (r.description ?? '').toLowerCase().includes(needle)
      );
    });
  }, [search, roles.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Role management"
        description="Click a role to inspect its permissions."
        actions={
          <button
            type="button"
            data-testid="open-new-role"
            onClick={() => setCreateOpen(true)}
            className="px-3 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark"
          >
            New role
          </button>
        }
      />

      {denied ? (
        <div
          data-testid="permission-required"
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
        >
          <code className="font-mono">Identity/readRole</code> permission required.
        </div>
      ) : roles.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          Identity service temporarily unavailable: {(roles.error as Error).message}
        </div>
      ) : null}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by role name or id…"
      />

      <div
        data-testid="role-list"
        className="bg-white border border-border rounded-xl overflow-hidden"
      >
        {roles.isPending ? (
          <p className="px-4 py-8 text-center text-sm text-muted">Loading roles…</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted">
            {search ? 'No roles match the filter.' : 'No roles found.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="w-6 px-4 py-2"></th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <>
                  <tr
                    key={r.id}
                    data-testid={`role-row-${r.id}`}
                    className="hover:bg-gray-50 cursor-pointer border-t border-border"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    <td className="px-4 py-3 text-muted">
                      {expanded === r.id ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-nipost-green" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{r.name}</p>
                          <p className="text-[10px] font-mono text-muted">{r.id}</p>
                        </div>
                        {r.isSystem ? (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-gray-100 text-muted rounded">
                            system
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted">{r.description ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {r.isSystem ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                          <Lock className="w-3 h-3" />
                          immutable
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`edit-role-${r.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(r);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-nipost-green border border-nipost-green rounded hover:bg-nipost-green-light"
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === r.id ? (
                    <tr key={`${r.id}-perms`}>
                      <td colSpan={4} className="p-0">
                        <PermissionsDrawer roleId={r.id} />
                      </td>
                    </tr>
                  ) : null}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-muted">
        Need to inspect per-field grants? See{' '}
        <Link
          to="/security/field-permissions"
          className="font-semibold text-nipost-green hover:underline"
        >
          field permissions
        </Link>
        .
      </p>

      {createOpen ? (
        <RoleEditorModal
          role={null}
          onClose={() => setCreateOpen(false)}
          onSaved={() =>
            void queryClient.invalidateQueries({ queryKey: ['identity', 'roles'] })
          }
        />
      ) : null}
      {editing ? (
        <RoleEditorModal
          role={editing}
          onClose={() => setEditing(null)}
          onSaved={() =>
            void queryClient.invalidateQueries({ queryKey: ['identity', 'roles'] })
          }
        />
      ) : null}
    </div>
  );
}
